import { useEffect, useRef } from 'react';

import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';

type LoginFormProps = {
    error: string;
    loading: boolean;
    onForgotPassword: () => void;
    onForgotUsername: () => void;
    onPasswordChange: (value: string) => void;
    onSubmit: () => void;
    onTogglePassword: () => void;
    onTurnstileToken: (token: string) => void;
    onUsernameChange: (value: string) => void;
    password: string;
    requireCaptcha: boolean;
    showPassword: boolean;
    turnstileSiteKey: string;
    username: string;
};

export const LoginForm = (props: LoginFormProps) => {

    const { formatMessage } = useIntl();

    const {
        error, loading, onForgotPassword, onForgotUsername, onPasswordChange,
        onSubmit, onTogglePassword,
        onTurnstileToken, onUsernameChange, password,
        requireCaptcha, showPassword, turnstileSiteKey, username
    } = props;

    const turnstileRef = useRef<HTMLDivElement>(null);
    const turnstileWidgetId = useRef<string | null>(null);

    useEffect(() => {

        if (requireCaptcha && turnstileSiteKey && turnstileRef.current && (window as any).turnstile) {

            if (turnstileWidgetId.current === null) {

                turnstileWidgetId.current = (window as any).turnstile.render(turnstileRef.current, {
                    callback: (token: string) => {

                        onTurnstileToken(token);

                    },
                    sitekey: turnstileSiteKey,
                    theme: 'light'
                });

            }

        }

    }, [requireCaptcha, turnstileSiteKey]);

    const handleKeyDown = (e: React.KeyboardEvent) => {

        if (e.key === 'Enter') {

            e.preventDefault();
            onSubmit();

        }

    };

    const getDownloadLink = () => {

        const host = window.location.host;
        let baseUrl = 'gmetrix.net';

        if (host.includes('devmanage')) baseUrl = 'dev.gmetrix.net';
        else if (host.includes('stagemanage')) baseUrl = 'stage.gmetrix.net';
        else if (host.includes('uatmanage')) baseUrl = 'uat.gmetrix.net';

        return `https://${baseUrl}/download-sms`;

    };

    return (
        <>
            <img
                alt={formatMessage({ id: 'gmetrix.a4da7596', defaultMessage: 'GMetrix' })}
                className="login-card-logo d-block mx-auto"
                src="https://content.gmetrix.net/images/GMetrix/GMetrix-Logo-RGB.png"
            />
            <div className="login-card-logo-text text-center">
                {<FormattedMessage defaultMessage="Skills Management System" id="skills_management_system.ee6cd159" /> || 'Skills Management System'}
            </div>
            <div className="login-card-title text-center fw-semibold mb-4">
                {<FormattedMessage defaultMessage="Administrator Login" id="administrator_login.8a4beda5" /> || 'Administrator Login'}
            </div>

            <div className="login-username-group mb-2">
                <GMetrixInput
                    autoFocus
                    autoComplete="off"
                    label={formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' }) || 'Username'}
                    onChange={e => onUsernameChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    tabIndex={1}
                    type="text"
                    value={username}
                />
                <div className="d-flex justify-content-end">
                    <a
                        className="login-form-link"
                        onClick={onForgotUsername}
                        tabIndex={4}
                    >
                        {<FormattedMessage defaultMessage="Forgot your username?" id="forgot_your_username.a27e4f4a" /> || 'Forgot your username?'}
                    </a>
                </div>
            </div>

            <label className="form-label fw-bold">
                {<FormattedMessage defaultMessage="Password" description={formatMessage({ id: 'formulario.6dc35e9d', defaultMessage: 'Formulario' })} id="password.19467d6e" /> || 'Password'}
            </label>
            <div className="login-password-wrapper position-relative mb-2">
                <GMetrixInput
                    autoComplete="off"
                    onChange={e => onPasswordChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    tabIndex={2}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                />
                <button
                    className="login-eye-btn"
                    onClick={onTogglePassword}
                    tabIndex={3}
                    type="button"
                >
                    <i className={`fa-light fa-lg ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`} />
                </button>
            </div>

            <div className="login-form-links-row d-flex justify-content-end mb-3">
                <a
                    className="login-form-link"
                    onClick={onForgotPassword}
                    tabIndex={4}
                >
                    {<FormattedMessage defaultMessage="Forgot your password?" description={formatMessage({ id: 'formulario.6dc35e9d', defaultMessage: 'Formulario' })} id="forgot_your_password.6bacd87e" /> || 'Forgot your password?'}
                </a>
            </div>

            {requireCaptcha && (
                <div className="d-flex justify-content-center mb-3" ref={turnstileRef} />
            )}

            <GMetrixButton
                className="login-sign-in-btn w-100"
                disabled={loading}
                onClick={onSubmit}
                tabIndex={5}
            >
                {loading ? (<FormattedMessage defaultMessage="Signing In…" id="signing_in.dfcecee5" /> || 'Signing In\u2026') : (<FormattedMessage defaultMessage="Sign In" id="sign_in.e435c365" /> || 'Sign In')}
            </GMetrixButton>

            <div className="login-external-links d-flex flex-column align-items-center pt-3 mt-4">
                <div className="login-external-icons d-flex align-items-center justify-content-center">
                    <a
                        className="login-external-link text-decoration-none d-inline-flex align-items-center"
                        href="http://support.gmetrix.net"
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        {<FormattedMessage defaultMessage="Help & Support" id="help_and_support.16326190" /> || 'Help & Support'}
                        <i aria-hidden="true" className="fa-solid fa-circle-question" />
                    </a>
                    <a
                        className="login-external-link text-decoration-none d-inline-flex align-items-center"
                        href={getDownloadLink()}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        {<FormattedMessage defaultMessage="Downloads" id="downloads.977082e0" /> || 'Downloads'}
                        <i aria-hidden="true" className="fa-solid fa-download" />
                    </a>
                </div>
                <a
                    className="login-external-portal-link text-decoration-none d-inline-flex align-items-center"
                    href="https://www.gmetrix.net/"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    {<FormattedMessage defaultMessage="Student Portal" id="student_portal.af79cd16" /> || 'Student Portal'}
                    <i aria-hidden="true" className="fa-solid fa-arrow-up-right-from-square" />
                </a>
            </div>
        </>
    );

};
